import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Tooltip,
  Button,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import StopIcon from '@mui/icons-material/Stop';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SecurityIcon from '@mui/icons-material/Security';
import {
  MediaRecorderState,
  ProgressModal,
  uploadToScreenApp,
  initializeMediaRecorder,
  stopMediaRecording,
  useIframeResizer,
} from './common';

interface WebcamMicTesterState extends MediaRecorderState {
  isMuted: boolean;
  elapsedTime: string;
  hasStartedTest: boolean;
}

const WebcamMicTester: React.FC = () => {
  const { t: translate } = useTranslation();
  useIframeResizer();

  const [state, setState] = useState<WebcamMicTesterState>({
    isRecording: false,
    isMuted: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
    elapsedTime: '00:00',
    hasStartedTest: false,
  });

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const videoStream = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const visualizerRef = useRef<HTMLCanvasElement>(null);
  const animationFrame = useRef<number | null>(null);

  const updateTimer = () => {
    const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const minutes = Math.floor(elapsedTime / 60);
    const seconds = elapsedTime % 60;
    setState((prev) => ({
      ...prev,
      elapsedTime: `${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
    }));
  };

  const drawVisualizer = () => {
    if (!visualizerRef.current || !analyser.current) return;

    const canvasCtx = visualizerRef.current.getContext('2d');
    if (!canvasCtx) return;

    const { width, height } = visualizerRef.current;
    const dataArray = new Uint8Array(analyser.current.frequencyBinCount);

    const draw = () => {
      if (!analyser.current) return;

      analyser.current.getByteFrequencyData(dataArray);
      canvasCtx.clearRect(0, 0, width, height);

      const barWidth = Math.ceil(
        (width / analyser.current.frequencyBinCount) * 2.5
      );
      const centerY = height / 2;
      let x = 0;

      canvasCtx.fillStyle = '#d1d5db';
      for (let i = 0; i < analyser.current.frequencyBinCount; i += 1) {
        const barHeight = Math.min(dataArray[i] * 1.5, height);
        canvasCtx.fillRect(
          Math.floor(x),
          Math.floor(centerY - barHeight / 2),
          Math.floor(barWidth),
          Math.floor(barHeight)
        );
        x += barWidth + 1;
      }

      animationFrame.current = requestAnimationFrame(draw);
    };

    draw();
  };

  const startRecording = async () => {
    try {
      videoStream.current = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        },
        audio: !state.isMuted,
      });

      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = videoStream.current;
      }

      // Set up audio analysis
      audioContext.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      analyser.current = audioContext.current.createAnalyser();
      const source = audioContext.current.createMediaStreamSource(
        videoStream.current
      );
      source.connect(analyser.current);

      analyser.current.fftSize = 2048;
      analyser.current.minDecibels = -90;
      analyser.current.maxDecibels = -10;
      analyser.current.smoothingTimeConstant = 0.85;

      mediaRecorder.current = new MediaRecorder(videoStream.current, {
        mimeType: 'video/webm;codecs=vp8,opus',
      });
      chunks.current = [];

      initializeMediaRecorder(mediaRecorder, chunks, (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      });

      mediaRecorder.current.start(200);
      startTimeRef.current = Date.now();
      timerInterval.current = setInterval(updateTimer, 1000);

      if (visualizerRef.current) {
        visualizerRef.current.style.display = 'block';
        drawVisualizer();
      }

      setState((prev) => ({ ...prev, isRecording: true }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: translate('webcammictester.messages.error'),
      }));
    }
  };

  const stopRecording = async () => {
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
    }

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') {
      return;
    }

    try {
      // Stop the recording
      stopMediaRecording(mediaRecorder, [videoStream.current]);

      // Create the blob with proper MIME type
      const blob = new Blob(chunks.current, {
        type: 'video/webm;codecs=vp8,opus',
      });

      // Upload the recording
      await uploadToScreenApp(
        blob,
        'webcam-recording.webm',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => {
          setState((prev) => ({
            ...prev,
            uploadComplete: true,
            uploading: false,
            error: null,
          }));

          // Clean up resources
          if (videoStream.current) {
            videoStream.current.getTracks().forEach((track) => track.stop());
            videoStream.current = null;
          }

          if (audioContext.current?.state !== 'closed') {
            audioContext.current?.close();
          }
          audioContext.current = null;
          analyser.current = null;
        },
        (error) =>
          setState((prev) => ({
            ...prev,
            error,
            uploading: false,
            uploadComplete: false,
          }))
      );

      if (visualizerRef.current) {
        visualizerRef.current.style.display = 'none';
      }

      setState((prev) => ({
        ...prev,
        isRecording: false,
        elapsedTime: '00:00',
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: translate('webcammictester.messages.error'),
        uploading: false,
        uploadComplete: false,
      }));
    }
  };

  const toggleMicrophone = () => {
    const newMutedState = !state.isMuted;
    setState((prev) => ({ ...prev, isMuted: newMutedState }));
    if (videoStream.current) {
      const tracks = videoStream.current.getAudioTracks();
      tracks.forEach((track) => {
        const newTrack = track;
        newTrack.enabled = !newMutedState;
      });
    }
  };

  const handleReset = () => {
    setState({
      isRecording: false,
      isMuted: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
      elapsedTime: '00:00',
      hasStartedTest: false,
    });

    // Clear visualizer
    if (visualizerRef.current) {
      const ctx = visualizerRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          visualizerRef.current.width,
          visualizerRef.current.height
        );
      }
      visualizerRef.current.style.display = 'none';
    }

    // Close audio context
    if (audioContext.current?.state !== 'closed') {
      audioContext.current?.close();
    }
    audioContext.current = null;
    analyser.current = null;
  };

  const startTest = async () => {
    setState((prev) => ({ ...prev, hasStartedTest: true }));
    await startRecording();
  };

  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
      if (videoStream.current) {
        videoStream.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContext.current?.state !== 'closed') {
        audioContext.current?.close();
      }
    };
  }, []);

  const iconButtonStyles = {
    minWidth: 36,
    width: 36,
    height: 36,
    padding: 0,
    borderRadius: 1.5,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  };

  const menuBarStyles = {
    bgcolor: 'background.paper',
    py: 1.5,
    px: { xs: 2, sm: 2.5 },
    borderRadius: 2,
    flexWrap: { xs: 'nowrap', sm: 'wrap' },
    gap: { xs: 2, sm: 2.5 },
    '& .MuiStack-root': {
      mx: 0,
    },
  };

  const renderInitialState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        textAlign: 'center',
        p: 3,
      }}
    >
      <VideocamIcon
        sx={{
          fontSize: 64,
          color: 'primary.main',
          mb: 3,
        }}
      />
      <Typography variant="h5" gutterBottom>
        {translate('webcammictester.messages.welcome')}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4, maxWidth: 600 }}
      >
        {translate('webcammictester.messages.instructions')}
      </Typography>
      <Stack spacing={2} alignItems="center">
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <SecurityIcon color="action" />
          <Typography variant="body2" color="text.secondary">
            {translate('webcammictester.messages.permissions')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<VideocamIcon />}
          onClick={startTest}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 28,
            fontSize: '1.1rem',
          }}
        >
          {translate('webcammictester.controls.startTest')}
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3, fontFamily: 'Inter' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {state.hasStartedTest ? (
          <Box
            sx={{
              width: '100%',
              minHeight: 200,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{
                ...menuBarStyles,
                mb: 2,
                mx: 2,
                mt: 2,
              }}
            >
              <Stack
                alignItems="center"
                sx={{
                  minWidth: { xs: 'auto', sm: 48 },
                }}
              >
                <Tooltip
                  title={
                    state.isRecording
                      ? translate('webcammictester.controls.stopRecording')
                      : translate('webcammictester.controls.startRecording')
                  }
                >
                  <IconButton
                    color={state.isRecording ? 'error' : 'primary'}
                    onClick={state.isRecording ? stopRecording : startRecording}
                    sx={{
                      ...iconButtonStyles,
                      ...(state.isRecording && {
                        bgcolor: 'error.main',
                        '& .MuiSvgIcon-root': {
                          color: '#FFFFFF !important',
                        },
                        '&:hover': {
                          bgcolor: 'error.dark',
                        },
                      }),
                    }}
                  >
                    {state.isRecording ? <StopIcon /> : <VideocamIcon />}
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    fontSize: '0.675rem',
                    mt: 0.75,
                    lineHeight: 1,
                    opacity: 0.9,
                  }}
                >
                  {state.isRecording
                    ? translate('webcammictester.controls.stopRecording')
                    : translate('webcammictester.controls.startRecording')}
                </Typography>
              </Stack>

              <Stack
                alignItems="center"
                sx={{
                  minWidth: { xs: 'auto', sm: 48 },
                }}
              >
                <Tooltip
                  title={
                    state.isMuted
                      ? translate('webcammictester.controls.unmuteMicrophone')
                      : translate('webcammictester.controls.muteMicrophone')
                  }
                >
                  <IconButton
                    onClick={toggleMicrophone}
                    color={state.isMuted ? 'default' : 'primary'}
                    sx={{
                      ...iconButtonStyles,
                      ...(state.isMuted && {
                        bgcolor: 'action.selected',
                      }),
                    }}
                  >
                    {state.isMuted ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    fontSize: '0.675rem',
                    mt: 0.75,
                    lineHeight: 1,
                    opacity: 0.9,
                  }}
                >
                  {state.isMuted
                    ? translate('webcammictester.controls.unmuteMicrophone')
                    : translate('webcammictester.controls.muteMicrophone')}
                </Typography>
              </Stack>
            </Stack>

            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16/9',
                bgcolor: 'black',
                borderRadius: 1,
                overflow: 'hidden',
                mx: 2,
              }}
            >
              <video
                ref={videoPreviewRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {state.isRecording && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <FiberManualRecordIcon
                    sx={{
                      color: 'error.main',
                      animation: 'pulse 1s infinite',
                      '@keyframes pulse': {
                        '0%': {
                          transform: 'scale(1)',
                        },
                        '100%': {
                          transform: 'scale(1.2)',
                        },
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: 'white', fontWeight: 'bold' }}
                  >
                    {state.elapsedTime}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              component="canvas"
              ref={visualizerRef}
              sx={{
                width: 'calc(100% - 32px)',
                height: 100,
                my: 2,
                mx: 2,
                display: 'none',
                bgcolor: 'background.default',
                borderRadius: 1,
              }}
            />

            {state.error && (
              <Typography
                color="error"
                variant="body2"
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  mx: 2,
                }}
              >
                {state.error}
              </Typography>
            )}
          </Box>
        ) : (
          renderInitialState()
        )}
      </Paper>

      <ProgressModal
        open={state.uploading || state.uploadComplete}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={translate('webcammictester.messages.processing')}
        completeMessage={translate('webcammictester.messages.complete')}
        onReset={handleReset}
      />
    </Box>
  );
};

export default WebcamMicTester;
